import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { deriveClientAccountStatus, useMeQuery } from '@/entities/user';
import { EmailVerificationStep } from '@/features/auth';
import { IdentityStep } from '@/features/identity';
import { getErrorMessage } from '@/shared/lib';
import { BlockButton } from '@/shared/ui';
import { AuthShell } from '@/widgets/auth-shell';

/**
 * 온보딩 라우팅 — 계정 상태(users.me 에서 파생)에 따라 남은 단계를 안내한다.
 *
 * 이메일 인증 → PASS 본인인증 → ACTIVE. 어느 경로(일반 가입 · 소셜)로 왔든
 * 상태만 보고 분기한다. (설계 §2-3 — 소셜 성공 응답은 "온보딩 진입")
 * 서버가 본인인증을 쓰지 않으면(identityRequired=false) 이메일 인증 뒤 바로 가입 완료이고 단계 번호도 없다.
 */
export const OnboardingPage = (): ReactElement => {
  const navigate = useNavigate();
  const me = useMeQuery();

  const refreshStatus = async (): Promise<void> => {
    await me.refetch();
  };

  if (me.isPending) {
    return (
      <AuthShell title="온보딩" description="상태를 확인하고 있습니다…">
        <p className="text-sm text-muted-foreground">잠시만 기다려 주세요.</p>
      </AuthShell>
    );
  }

  if (me.isError) {
    return (
      <AuthShell title="온보딩" description="상태를 불러오지 못했습니다.">
        <p
          role="alert"
          className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {getErrorMessage(me.error, '잠시 후 다시 시도해 주세요.')}
        </p>
      </AuthShell>
    );
  }

  const status = deriveClientAccountStatus(me.data);

  if (status === 'PENDING_EMAIL') {
    return (
      <AuthShell
        title="이메일 인증"
        description={
          me.data.identityRequired
            ? '1단계 — 이메일 주소를 확인해 주세요.'
            : '이메일 주소를 확인해 주세요.'
        }
      >
        <EmailVerificationStep onRefresh={refreshStatus} />
      </AuthShell>
    );
  }

  if (status === 'PENDING_IDENTITY') {
    return (
      <AuthShell title="본인인증" description="2단계 — 본인인증을 완료해 주세요.">
        <IdentityStep onDone={refreshStatus} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="가입 완료" description="모든 인증이 끝났습니다.">
      <BlockButton type="button" onClick={() => void navigate('/', { replace: true })}>
        서비스 시작하기
      </BlockButton>
    </AuthShell>
  );
};
